import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  type UseQueryOptions,
  type UseSuspenseInfiniteQueryOptions,
  type UseSuspenseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { serialize } from 'object-to-formdata';
import type { Endpoints } from 'types';
import { downloadTextFile } from 'utils/fileUtils';
import { type ApiError, faksApiInstance } from './api';
import { type AlgorithmParams, useIdempotencyRefs } from './idempotency';

type EnhancedRoute<Route extends keyof Endpoints> = Endpoints[Route]['PATH_PARAMS'] extends never
  ? Route
  : {
      route: Route;
      pathParams: Endpoints[Route]['PATH_PARAMS'];
    };

export const applyPathParams = <Route extends keyof Endpoints>(route: EnhancedRoute<Route>) => {
  if (typeof route === 'string') return route;

  let finalRoute: string = route.route;
  Object.entries(route.pathParams).forEach(([key, value]) => {
    finalRoute = finalRoute.replace(`:${key}`, value.toString());
  });

  return finalRoute;
};

export const keys = <Route extends keyof Endpoints>(route: EnhancedRoute<Route>) => ({
  all: [route] as const,
  //lists: [route, 'list'] as const,
  list: <T extends 'paginated' | 'infinite'>(
    type: T,
    params?: T extends 'infinite'
      ? Omit<Endpoints[Route]['INDEX']['params'], 'page'>
      : Endpoints[Route]['INDEX']['params']
  ) => [route, 'list', { type }, params] as const,
  statistics: (params?: Endpoints[Route]['STATISTICS']['params']) =>
    [route, 'statistics', params] as const,
  //details: [route , 'detail'] as const,
  detail: (id: number | null | undefined) => [route, 'detail', id] as const,
});

export const useInfiniteIndexQuery = <
  Route extends keyof Endpoints,
  TQueryFnData extends Endpoints[Route]['INDEX']['response'],
  TError = AxiosError<ApiError>,
  TData = TQueryFnData,
>(
  route: EnhancedRoute<Route>,
  params?: Omit<Endpoints[Route]['INDEX']['params'], 'page'>,
  options?: Omit<
    UseInfiniteQueryOptions<TQueryFnData, TError, InfiniteData<TData>>,
    'initialPageParam' | 'getNextPageParam' | 'queryKey' | 'queryFn'
  >
) => {
  const finalRoute = applyPathParams(route);

  return useInfiniteQuery({
    queryKey: keys(route).list('infinite', params),
    queryFn: ({ pageParam = 1 }) =>
      faksApiInstance.get<TQueryFnData>(finalRoute, {
        params: { page: pageParam, ...params },
      }),
    enabled: !!params,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageItems = Object.values(lastPage)[0];

      if (!lastPageItems || lastPageItems.length === 0) return undefined;

      return allPages.length + 1;
    },
    ...options,
  });
};

/** @public This is going to be used soon */
export const useSuspenseInfiniteIndexQuery = <
  Route extends keyof Endpoints,
  TQueryFnData extends Endpoints[Route]['INDEX']['response'],
  TError = AxiosError<ApiError>,
  TData = TQueryFnData,
>(
  route: EnhancedRoute<Route>,
  params?: Omit<Endpoints[Route]['INDEX']['params'], 'page'>,
  options?: Omit<
    UseSuspenseInfiniteQueryOptions<TQueryFnData, TError, InfiniteData<TData>>,
    'initialPageParam' | 'getNextPageParam' | 'queryKey' | 'queryFn'
  >
) => {
  const finalRoute = applyPathParams(route);

  return useSuspenseInfiniteQuery({
    queryKey: keys(route).list('infinite', params),
    queryFn: ({ pageParam = 1 }) =>
      faksApiInstance.get<TQueryFnData>(finalRoute, {
        params: { page: pageParam, ...params },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageItems = Object.values(lastPage)[0];

      if (!lastPageItems || lastPageItems.length === 0) return undefined;

      return allPages.length + 1;
    },
    ...options,
  });
};

export const useIndexQuery = <
  Route extends keyof Endpoints,
  TQueryFnData extends Endpoints[Route]['INDEX']['response'],
  TError = AxiosError<ApiError>,
  TData = TQueryFnData,
>(
  route: EnhancedRoute<Route>,
  params?: Endpoints[Route]['INDEX']['params'],
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'>
) => {
  const finalRoute = applyPathParams(route);

  return useQuery({
    queryKey: keys(route).list('paginated', params),
    queryFn: () => faksApiInstance.get<TQueryFnData>(finalRoute, { params }),
    enabled: !!params,
    ...options,
  });
};

export const useSuspenseIndexQuery = <
  Route extends keyof Endpoints,
  TQueryFnData extends Endpoints[Route]['INDEX']['response'],
  TError = AxiosError<ApiError>,
  TData = TQueryFnData,
>(
  route: EnhancedRoute<Route>,
  params?: Endpoints[Route]['INDEX']['params'],
  options?: Omit<UseSuspenseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'>
) => {
  const finalRoute = applyPathParams(route);

  return useSuspenseQuery({
    queryKey: keys(route).list('paginated', params),
    queryFn: () => faksApiInstance.get<TQueryFnData>(finalRoute, { params }),
    ...options,
  });
};

export const useMaybeSuspenseIndexQuery = <
  Route extends keyof Endpoints,
  TQueryFnData extends Endpoints[Route]['INDEX']['response'] | null,
  TError = AxiosError<ApiError>,
  TData = TQueryFnData,
>(
  route: EnhancedRoute<Route>,
  params?: Endpoints[Route]['INDEX']['params'],
  options?: Omit<
    UseSuspenseQueryOptions<TQueryFnData | null, TError, TData>,
    'queryKey' | 'queryFn'
  > & { enabled?: boolean }
) => {
  const finalRoute = applyPathParams(route);
  const { enabled = true, ...queryOptions } = options || {};

  return useSuspenseQuery({
    queryKey: [keys(route).list('paginated', params), ...(enabled ? [] : ['disabled'])],
    queryFn: enabled ? () => faksApiInstance.get<TQueryFnData>(finalRoute, { params }) : () => null,
    ...queryOptions,
  });
};

export const useStatisticsQuery = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  params?: Endpoints[Route]['STATISTICS']['params'],
  options?: Omit<
    UseQueryOptions<
      Endpoints[Route]['STATISTICS']['response'],
      AxiosError<ApiError>,
      Endpoints[Route]['STATISTICS']['response'],
      readonly [
        EnhancedRoute<Route>,
        'statistics',
        Endpoints[Route]['STATISTICS']['params'] | undefined,
      ]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const finalRoute = applyPathParams(route);

  return useQuery({
    queryKey: keys(route).statistics(params),
    queryFn: () =>
      faksApiInstance.get<Endpoints[Route]['STATISTICS']['response']>(`${finalRoute}/statistics`, {
        params,
      }),
    enabled: !!params,
    ...options,
  });
};

export const useShowQuery = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  id: number | undefined | null,
  options?: Omit<
    UseQueryOptions<
      Endpoints[Route]['SHOW']['response'],
      AxiosError<ApiError>,
      Endpoints[Route]['SHOW']['response'],
      readonly [EnhancedRoute<Route>, 'detail', number | null | undefined]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const finalRoute = applyPathParams(route);

  return useQuery({
    queryKey: keys(route).detail(id),
    queryFn: () => faksApiInstance.get<Endpoints[Route]['SHOW']['response']>(`${finalRoute}/${id}`),
    enabled: !!id,
    ...options,
  });
};

export const useSuspenseShowQuery = <
  Route extends keyof Endpoints,
  TQueryFnData extends Endpoints[Route]['SHOW']['response'],
  TError = AxiosError<ApiError>,
  TData = TQueryFnData,
>(
  route: EnhancedRoute<Route>,
  id: number | undefined | null,
  options?: Omit<UseSuspenseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'>
) => {
  const finalRoute = applyPathParams(route);

  return useSuspenseQuery({
    queryKey: keys(route).detail(id),
    queryFn: () => faksApiInstance.get<TQueryFnData>(`${finalRoute}/${id}`),
    ...options,
  });
};

export const useCreateMutation = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  options?: UseMutationOptions<
    Endpoints[Route]['CREATE']['response'],
    AxiosError<ApiError>,
    Endpoints[Route]['CREATE']['params']
  >,
  needsSerialization?: boolean,
  idempotencyAlgorithm?: (params: AlgorithmParams<Endpoints[Route]['CREATE']['params']>) => string,
  config?: AxiosRequestConfig
) => {
  const client = useQueryClient();
  const finalRoute = applyPathParams(route);
  const { prevPayloadRef, idempotencyKeyRef } =
    useIdempotencyRefs<Endpoints[Route]['CREATE']['params']>();

  return useMutation({
    mutationFn: (params) => {
      const idempotencyKey = idempotencyAlgorithm?.({
        payload: params,
        route: finalRoute,
        prevPayloadRef,
        idempotencyKeyRef,
      });

      const requestConfig = idempotencyKey
        ? { ...config, headers: { ...config?.headers, 'Idempotency-Key': idempotencyKey } }
        : config;

      return faksApiInstance.post<Endpoints[Route]['CREATE']['response']>(
        finalRoute,
        needsSerialization ? serialize(params) : params,
        requestConfig
      );
    },

    ...options,

    onSuccess: (data, ...args) => {
      options?.onSuccess?.(data, ...args);
      client.invalidateQueries({ queryKey: keys(route).all });
    },
  });
};

export const useExportMutation = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  filename?: string,
  options?: UseMutationOptions<
    Endpoints[Route]['EXPORT']['response'],
    AxiosError<ApiError>,
    Endpoints[Route]['EXPORT']['params']
  >
) => {
  const finalRoute = applyPathParams(route);

  return useMutation({
    mutationFn: (params) =>
      faksApiInstance.get<Endpoints[Route]['EXPORT']['response']>(finalRoute, { params }),
    ...options,

    onSuccess: (data, ...args) => {
      downloadTextFile({
        fileName: filename || data.filename,
        content: atob(data.file),
        mimeType: '',
      });
      options?.onSuccess?.(data, ...args);
    },
  });
};

export const useUpdateMutation = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  options?: UseMutationOptions<
    Endpoints[Route]['UPDATE']['response'],
    AxiosError<ApiError>,
    Endpoints[Route]['UPDATE']['params']
  >,
  idempotencyAlgorithm?: (params: AlgorithmParams<Endpoints[Route]['UPDATE']['params']>) => string,
  config?: AxiosRequestConfig
) => {
  const client = useQueryClient();
  const finalRoute = applyPathParams(route);
  const { prevPayloadRef, idempotencyKeyRef } =
    useIdempotencyRefs<Endpoints[Route]['UPDATE']['params']>();

  return useMutation({
    mutationFn: (params) => {
      const idempotencyKey = idempotencyAlgorithm?.({
        payload: params,
        route: finalRoute,
        prevPayloadRef,
        idempotencyKeyRef,
      });

      const requestConfig = idempotencyKey
        ? { ...config, headers: { ...config?.headers, 'Idempotency-Key': idempotencyKey } }
        : config;

      return faksApiInstance.patch<Endpoints[Route]['UPDATE']['response']>(
        'id' in params ? `${finalRoute}/${params.id}` : finalRoute,
        params.options?.needsSerialization ? serialize(params.fields) : params.fields,
        requestConfig
      );
    },

    ...options,

    onSuccess: (data, ...args) => {
      client.invalidateQueries({ queryKey: keys(route).all });
      options?.onSuccess?.(data, ...args);
    },
  });
};

export const useDestroyMutation = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  options?: UseMutationOptions<
    Endpoints[Route]['DESTROY']['response'],
    AxiosError<ApiError>,
    number | undefined
  >
) => {
  const client = useQueryClient();
  const finalRoute = applyPathParams(route);

  return useMutation({
    mutationFn: (id) =>
      faksApiInstance.delete<Endpoints[Route]['DESTROY']['response']>(
        id ? `${finalRoute}/${id}` : finalRoute
      ),
    ...options,

    onSuccess: (data, ...args) => {
      // defer allows to call instructions before invalidation refetchs,
      // for example, allow navigation before refetch to prevent 404 errors)
      setTimeout(() => client.invalidateQueries({ queryKey: keys(route).all }), 1);
      options?.onSuccess?.(data, ...args);
    },
  });
};
